import { Component, ViewChild, inject } from '@angular/core';
import { AppConfig, FormComponent, NoticeService, Redirection } from '@tytaccounts/common';
import {
  FormHandlingComponent,
  FormInput,
  Page, PageComponent
} from '@tytapp/common';
import { CreateStripeTokenResponse, StripeService } from '@tytapp/stripe';
import { UserService } from '@tytapp/user';

import { NgZone } from '@angular/core';
import { ApiMembership, ApiPaymentSource, ApiUser, UsersApi } from '@tytapp/api';
import { environment } from 'environments/environment';
import {
  CVCValidator,
  CreditCardService,
  CreditCardValidator,
  ExpirationDateValidator,
  NonEmptyValidator,
  Validated
} from 'validation';

@Page('updateBilling')
@Component({
  selector: 'app-update-billing',
  templateUrl: './update-billing.component.html',
  styleUrls: ['./update-billing.component.scss']
})
export class UpdateBillingComponent extends PageComponent implements FormHandlingComponent {
  private stripe = inject(StripeService);
  private creditCards = inject(CreditCardService);
  private userService = inject(UserService);
  private notices = inject(NoticeService);
  private zone = inject(NgZone);
  private redirection = inject(Redirection);
  private usersApi = inject(UsersApi);
  private appConfig = inject( AppConfig);

  @Validated({ class: NonEmptyValidator, field: 'cardholder-name' })
  @FormInput()
  cardholderName: string = "";

  @Validated({ class: CreditCardValidator, field: 'cc-number' })
  @FormInput()
  creditCard: string = "";

  @Validated({ class: ExpirationDateValidator, field: 'cc-exp' })
  @FormInput()
  expiration: string = "";

  @Validated({ class: CVCValidator, field: 'cc-csc' })
  @FormInput()
  cvc: string = "";

  @FormInput()
  address_zip: string = "";

  user: ApiUser;
  paymentSource: ApiPaymentSource;
  updateApplePay: boolean = true;
  straightToCC: boolean = false;
  submitting: boolean = false;
  cardBrand: string = null;
  cardBrandIcon: string = null;
  validityError: boolean;
  gatewayName: string = 'stripe';
  applePay: boolean = false;
  pleaseWait: boolean = true;
  pleaseWaitMessage : string = 'Please wait...';
  switchToAppleOption: boolean = false;
  paymentType : string;
  paymentUrl: string;
  paypalSub: boolean = false;

  clientLoaded = false;
  updateForGifts = false;

  get membershipGateway() {
    if (!this.user || !this.user.membership)
      return null;
    if (this.user.membership.gateway_name == 'stripe' && this.user.membership.gift_item)
      return 'renewing-gift';
    if (this.user.membership.gateway_name == 'free' && this.user.membership.gift_item)
      return 'fixed-gift';

    if (this.updateForGifts)
      return 'stripe';

    return this.user.membership.gateway_name;
  }

  get isDeveloper() {
    if (!this.appConfig)
      return false;
    return (environment.debug);
  }

  async init() {
    this.pageTitle = 'Update Billing';

    if (typeof window !== 'undefined')
      this.clientLoaded = true;

    await this.stripe.apiReady();

    this.subscribe(this.userService.userChanged, async user => {
      if (!user) {
        this.userService.goToLogin();
        return;
      }

      this.user = user;

      try {
        this.paymentSource = await this.usersApi.getPaymentSource().toPromise();
      } catch (e) {
        if (e.status === 404) {
          this.paymentSource = null;
        } else {
          if (e.json)
            e = e.json();
          console.error(`Failed to fetch current payment source:`);
          console.error(e);
        }
      }
      this.pleaseWait = false;

      if (!user.membership)
        this.updateForGifts = true;

      if (this.payingWithApplePay)
        this.stripePaymentMethod = 'apple_pay';
      else
        this.stripePaymentMethod = 'credit_card';

      this.stripe.applePay.checkAvailability(available => {
        this.applePayAvailable = available;

        // ** IF APPLE PAY IS SET UP ON SAFARI BUT USER NOT ON IT -- GIVE SWITCH
        if (this.user.membership) {
          if (this.user.membership.payment_method === 'apple_pay') {
            this.applePay = true;
            this.paymentType = 'Apple Pay';
            this.paymentUrl = 'http://appleid.apple.com';
          } else if (available) {
            this.paymentType = 'Credit Card';
            this.switchToAppleOption = true;
          } else {  // payment_method === 'credit_card'
            this.updateApplePay = false;
            this.straightToCC = true;
          }
        } else {
          this.updateApplePay = false;
          this.straightToCC = true;
        }
      });
    });
  }

  switchStripeMethod(method : 'credit_card' | 'apple_pay') {
    this.stripePaymentMethod = method;
    this.hideError();
  }

  get ownMembershipIsStripe() {
    if (!this.user || !this.user.membership)
      return false;
    if (this.user.membership.gateway_name == 'stripe')
      return true;
    return false;
  }

  applePayAvailable : boolean = false;
  stripePaymentMethod : 'credit_card' | 'apple_pay' = 'credit_card';

  @ViewChild('form')
  form: FormComponent;

  /**
   * Called when submitting a credit card directly
   */
  async submit() {
    if (!this.form.validate())
      return;
    if (this.pleaseWait)
      return;
    this.pleaseWait = true;
    this.pleaseWaitMessage = 'Updating your billing details...';

    // get stripe token

    let message = `
      Changing your payment source will update all renewing TYT memberships that are associated with your 
      account (including any renewing gifts that are active). Would you like to continue?'
    `;

    if (await this.shell.confirm('Please note', message)) {
      let token: CreateStripeTokenResponse;
      let cardData : any = {
        name: this.cardholderName,
        number: this.creditCard,
        exp: this.expiration,
        cvc: this.cvc
      };

      if (this.address_zip && this.address_zip != '')
        cardData.address_zip = this.address_zip;

      try {
        token = await this.stripe.createToken(cardData);
      } catch (e) {
        this.pleaseWait = false;
        this.showError(e.message || e.error);
        return;
      }

      // Clear the credit card data out of the page as soon as we can (also forces users to re-enter upon failure, preventing them from missing a mistake on multiple attempts)
      this.cardholderName = this.creditCard = this.expiration = this.cvc = '';

      if (token.response.error && token.response.error.message) {
        this.pleaseWait = false;
        this.showError(token.response.error.message);
        return;
      }

      // Update Membership
      let membership: ApiMembership;
      try {
        membership = await this.usersApi.changePaymentSource({ gateway_name: 'stripe', gateway_data: <any>{ token: token.response.id } }).toPromise();

        this.userService.billingUpdated(membership);
        this.notices.post('Your billing information has been updated.', 'info');
        this.redirection.go('/billing/update/success');

      } catch(e) {
        if (e.json)
          e = e.json();
        console.error("Error occured with updating billing info: ");
        console.error(e);

        let message = e.message || e.error || 'An unknown error occurred';

        this.showError(`Could not update your billing information: ${message} -- If this issue persists please contact support@tytnetwork.com`);
        this.pleaseWait = false;
      }

    }

  }

  /**
   * Called when initiating an Apple Pay session
   */
  async updateWithApplePay() {
    if (this.pleaseWait)
      return;
    this.pleaseWait = true;
    this.pleaseWaitMessage = 'Waiting for Apple Pay';

    let period = this.user.membership.product.period === 'monthly' ? 'monthly': 'annual';

    //await new Promise((res, rej) => {
    //  setTimeout(() => res(), 5000);
    //});

    try {
      let session = this.stripe.applePay.buildSession(
        {
          total: {
            label: `on ${period} renewal date`,
            amount: this.user.membership.product.dollar_price
          },
          countryCode: 'US',
          currencyCode: 'USD'

        },
        async (result, completion) => {
          let membership: ApiMembership;
          let token = result.token.id;

          try {
            membership = await this.usersApi.changePaymentSource({ gateway_name: this.gatewayName, gateway_data: <any>{ token } }).toPromise();
            this.userService.billingUpdated(membership);
            completion(true);
            this.notices.post('Your billing information has been updated.', 'info')
            this.redirection.go('/settings/memberships')
          } catch (e) {
            console.error('Caught exception while updating billing info:');
            console.error(e);

            this.showError(e.message || e.error || "An error occured while processing your request.");
            this.pleaseWait = false;
            completion(false);
          }
        },
        (e) => {
          console.error('Error caught during Apple Pay buildSession():');
          console.error(e);
          this.pleaseWait = false;
          this.showError(e.message);
        }
      );

      session.oncancel = () => {
        this.zone.run(() => {
          // User closed the Apple Pay session
          setTimeout(() => {
            this.pleaseWait = false;
          });
        });
      };

      session.begin();
    } catch (e) {
      let message : string = e.message;

      if (message.includes('not supported')) {
        message = 'Apple Pay is not supported by this browser. Please log into your account on Safari on an iOS/macOS device enrolled in Apple Pay and try again.';
      } else {
        message = 'An error occurred while contacting Apple Pay. If this issue persists, please contact support@tytnetwork.com';
      }

      console.error(`Failed to contact Apple Pay:`)
      console.error(e);

      this.showError(message);

      this.pleaseWait = false;
    }

  }

  get payingWithApplePay() {
    if (this.paymentSource && this.paymentSource.tokenization_method == 'apple_pay')
      return true;

    if (!this.user.membership)
      return false;
    if (this.user.membership.gateway_name != 'stripe')
      return false;
    if (this.user.membership.payment_method == 'apple_pay')
      return true;

    return false;
  }

  backToAccountsPage() {
    // this.location.back();
    this.redirection.go('/membershipAccount')
  }


  cancelUpdateInfo() {
    if (this.straightToCC) {
      // this.location.back();
      this.redirection.go('/membershipAccount')
    } else {
      this.updateApplePay = true
    }
  }

  updateCardBrand() {
    setTimeout(() => {
      let cardStr = this.creditCard;
      let cardInfo = this.creditCards.getInfo(cardStr);
      this.cardBrand = cardInfo && cardInfo.card ? cardInfo.card.type : null;

      this.cardBrandIcon = {
        'visa': 'cc-visa',
        'master-card': 'cc-mastercard',
        'discover': 'cc-discover',
        'american-express': 'cc-amex',
        'jcb': 'cc-jcb',
        'diners-club': 'cc-diners-club',
      }[this.cardBrand];
    }, 100);
  }

}
